import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, InjectionToken, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TermsLanguage } from '@core/models/terms/terms-version.model';
import { TermsService } from '@core/services/terms/terms.service';

/**
 * The page GBE renders is a whole document (its own head, CSS and script), so it replaces this
 * one rather than being embedded. A token so tests do not wipe the test runner's document.
 */
export const TERMS_DOCUMENT_WRITER = new InjectionToken<(html: string) => void>(
  'TERMS_DOCUMENT_WRITER',
  {
    factory: () => {
      const document = inject(DOCUMENT);
      return (html: string) => {
        document.open();
        document.write(html);
        document.close();
      };
    }
  }
);

type Failure = 'none' | 'unavailable';

// The page stands outside the app's translations: its language comes from the URL.
const MESSAGES: Record<TermsLanguage, Record<Failure, string> & { login: string }> = {
  fr: {
    none: "Aucune condition d'utilisation n'est en vigueur pour le moment.",
    unavailable:
      'Les conditions d’utilisation sont momentanément indisponibles. Réessayez plus tard.',
    login: 'Se connecter'
  },
  en: {
    none: 'No terms of use are in force at the moment.',
    unavailable: 'The terms of use are temporarily unavailable. Please try again later.',
    login: 'Log in'
  }
};

@Component({
  selector: 'app-terms-page',
  templateUrl: './terms-page.component.html',
  styles: ``
})
export class TermsPageComponent implements OnInit {
  private readonly termsService = inject(TermsService);
  private readonly writeDocument = inject(TERMS_DOCUMENT_WRITER);
  private readonly language = inject(ActivatedRoute).snapshot.paramMap.get(
    'lang'
  ) as TermsLanguage;

  protected readonly messages = MESSAGES[this.language];
  protected readonly failure = signal<Failure | null>(null);

  ngOnInit(): void {
    this.termsService.getCurrentTerms$(this.language).subscribe({
      next: (page) => this.writeDocument(page.html),
      error: (error: HttpErrorResponse) =>
        this.failure.set(error.status === 404 ? 'none' : 'unavailable')
    });
  }
}
